import { createSelector } from 'reselect';

import { MOET_CONSTANTS } from 'moet-core';

import {
  getForcedRegistrationIdCountries,
  getForcedVatIdCountries,
  getEnrichedVatCountries,
} from '@/selectors/options';
import { STAKEHOLDER_ROLES_V2 } from '@/containers/withStakeholderLogic';
import {
  getFrontendTypeFromQueryParam,
  isManagedKyc,
  isUnmanagedKyc,
  isWidget,
} from '@/selectors/widget';

import {
  getBankAccounts,
  getBusinessDetails,
  getEntityTypesAvailableForMerchant,
  getMerchantStatus,
  getOrganizationEntityTypesInMerchantCountry,
  getStakeholders,
  getStakeholdersByRole,
  hasSelectedProduct,
  isMerchantRegistered,
  merchantRegion,
  getMappedCountryConfig,
} from '@/selectors/merchant';

import { getAfterFlowStep } from '../merchant-application/common';
import { hasDocuments, hasMandatoryDocuments } from './documents';

export * from './documents';

const {
  ENTITY_TYPE,
  ONBOARDING_STATUS_VALUES_V2,
  ONBOARDING_STATUSES_V2,
  ORGANIZATION_ENTITY_TYPE,
  WIDGET_TYPE,
} = MOET_CONSTANTS;

const { ULTIMATE_BENEFICIAL_OWNER } = STAKEHOLDER_ROLES_V2;

export {
  hasHighExposure,
  hasSelectedProduct,
  isMerchantRegistered,
  shouldRenderPMLQuestionnaires,
} from '@/selectors/merchant';

export const isNativeKyc = createSelector(
  getFrontendTypeFromQueryParam,
  widgetType => WIDGET_TYPE.NATIVE_KYC === widgetType,
);

export const hasRegistrationInformation = createSelector(
  getBusinessDetails,
  ({ registered, registrationId = null }) =>
    (registered === true && !!registrationId) ||
    (registered === false && [null, ''].includes(registrationId)),
);

export const hasVatInformation = createSelector(
  getBusinessDetails,
  ({ subjectToVat, vatId = null }) =>
    (subjectToVat === true && !!vatId) || (subjectToVat === false && [null, ''].includes(vatId)),
);

export const hasEntityTypeInfo = createSelector(
  getBusinessDetails,
  getEntityTypesAvailableForMerchant,
  ({ entityType, ...rest }, entityTypes = []) => {
    const hasAllowedEntityType = entityTypes.find(e => e.key === entityType) !== undefined;
    return !!entityType && hasAllowedEntityType;
  },
);

export const hasNoEntityTypeInfo = createSelector(
  getBusinessDetails,
  getEntityTypesAvailableForMerchant,
  ({ entityType, ...rest }, entityTypes = []) => {
    const hasAllowedEntityType = entityTypes.find(e => e.key === entityType) !== undefined;
    return !entityType || !hasAllowedEntityType;
  },
);

export const hasOrganizationEntityTypeInfo = createSelector(
  getBusinessDetails,
  getOrganizationEntityTypesInMerchantCountry,
  ({ organizationEntityType, ...rest }, organizationEntityTypes = []) => {
    const hasAllowedEntityType =
      organizationEntityTypes.find(e => e.key === organizationEntityType) !== undefined;
    return !!organizationEntityType && hasAllowedEntityType;
  },
);

export const hasNoOrganizationEntityTypeInfo = createSelector(
  getBusinessDetails,
  getOrganizationEntityTypesInMerchantCountry,
  ({ organizationEntityType, ...rest }, organizationEntityTypes = []) => {
    const hasAllowedEntityType =
      organizationEntityTypes.find(e => e.key === organizationEntityType) !== undefined;
    return !organizationEntityType || !hasAllowedEntityType;
  },
);

export const isOrganizationEntityTypeSoleProprietorship = createSelector(
  getBusinessDetails,
  ({ organizationEntityType }) =>
    organizationEntityType === ORGANIZATION_ENTITY_TYPE.SOLE_PROPRIETORSHIP,
);

export const isOrganizationEntityTypeNotSoleProprietorship = createSelector(
  getBusinessDetails,
  ({ organizationEntityType }) =>
    organizationEntityType !== undefined &&
    organizationEntityType !== ORGANIZATION_ENTITY_TYPE.SOLE_PROPRIETORSHIP,
);

export const hasStoreDetails = createSelector(
  getBusinessDetails,
  ({ storeName, website, goodsCategory }) => {
    return !!storeName && !!website && !!goodsCategory;
  },
);

export const hasPartnershipDetails = createSelector(
  getBusinessDetails,
  ({ companyName, operatingAddress }) => {
    return !!companyName && !!operatingAddress;
  },
);

export const hasApplicant = createSelector(
  getStakeholders,
  stakeholders =>
    stakeholders.find(({ roles = [] }) => roles.includes(STAKEHOLDER_ROLES_V2.APPLICANT)) !==
    undefined,
);

export const hasNoApplicant = createSelector(
  getStakeholders,
  stakeholders =>
    stakeholders.find(({ roles = [] }) => roles.includes(STAKEHOLDER_ROLES_V2.APPLICANT)) ===
    undefined,
);

export const hasControlOverManagementStakeholders = createSelector(
  getStakeholders,
  stakeholders =>
    stakeholders.find(({ roles = [] }) =>
      roles.includes(STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT),
    ) !== undefined,
);

export const hasUltimateBeneficialOwner = createSelector(
  getStakeholders,
  stakeholders =>
    stakeholders.find(
      ({ roles = [] }) =>
        roles.includes(STAKEHOLDER_ROLES_V2.ULTIMATE_BENEFICIAL_OWNER) &&
        !roles.includes(STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT),
    ) !== undefined,
);

export const getDefaultRoleConstraints = createSelector(
  getMappedCountryConfig,
  ({ ROLE_CONSTRAINTS }) => ROLE_CONSTRAINTS,
);

/**
 * Whether the max limit of allowed stakeholders with UBO role is reached already, counting only
 * those stakeholders who have other roles as well.
 */
export const hasMaxUBOsWithMultipleRoles = createSelector(
  getStakeholdersByRole(ULTIMATE_BENEFICIAL_OWNER),
  getDefaultRoleConstraints,
  (
    stakeholders = [],
    { [ULTIMATE_BENEFICIAL_OWNER]: { max = Number.MAX_SAFE_INTEGER } = {} } = {},
  ) =>
    // All UBO stakeholders with more than 2 roles ([UBO, OWNER]) are counted
    stakeholders.filter(s => s.roles.length > 2).length >= max,
);

export const hasPartnerRole = createSelector(getStakeholders, stakeholders => {
  return (
    stakeholders.find(({ roles = [] }) => roles.includes(STAKEHOLDER_ROLES_V2.PARTNER)) !==
    undefined
  );
});

export const hasBankAccounts = createSelector(
  getBankAccounts,
  bankAccounts => bankAccounts.length > 0,
);

export const isEntityTypePartnership = createSelector(
  getBusinessDetails,
  ({ entityType }) => entityType === ENTITY_TYPE.PARTNERSHIP,
);

export const hasCompanyDetails = createSelector(
  getBusinessDetails,
  isEntityTypePartnership,
  ({ companyName }, isEntityTypePartnership) => !!companyName && !isEntityTypePartnership,
);

export const isEntityTypeSoleTrader = createSelector(
  getBusinessDetails,
  ({ entityType }) => entityType === ENTITY_TYPE.SOLE_TRADER,
);

export const isEntityTypePublicListedCompany = createSelector(
  getBusinessDetails,
  ({ entityType }) => entityType === ENTITY_TYPE.PUBLIC_LISTED_COMPANY,
);

export const isEntityTypePrivateCompany = createSelector(
  getBusinessDetails,
  ({ entityType }) => entityType === ENTITY_TYPE.PRIVATE_COMPANY,
);

export const isEntityTypeNotPrivateCompany = createSelector(
  getBusinessDetails,
  ({ entityType }) => entityType !== ENTITY_TYPE.PRIVATE_COMPANY,
);

export const isApplicant = createSelector(() => ({ roles = [] }) =>
  roles.includes(STAKEHOLDER_ROLES_V2.APPLICANT),
);

export const isApplicantOnly = createSelector(() => ({ roles = [] }) =>
  roles.includes(STAKEHOLDER_ROLES_V2.APPLICANT) && roles.length === 1,
);

export const hasSoleTraderInfo = createSelector(
  getStakeholders,
  (stakeholders = []) =>
    stakeholders.length === 1 &&
    stakeholders.find(
      ({ roles = [] }) => roles.length === 1 && roles.includes(STAKEHOLDER_ROLES_V2.SOLE_TRADER),
    ),
);

export const hasSoleProprietorshipInfo = createSelector(
  getStakeholders,
  (stakeholders = []) =>
    stakeholders.length === 1 &&
    !!stakeholders.find(
      ({ roles = [] }) => roles.length === 1 && roles.includes(STAKEHOLDER_ROLES_V2.OWNER),
    ),
);

export const hasAtLeastOneStakeholder = createSelector(
  getStakeholders,
  (stakeholders = []) => stakeholders.length > 0,
);

export const hasSubmittedStakeholderBlockUs = hasAtLeastOneStakeholder;

export const hasSubmittedStakeholderBlockSfsa = createSelector(
  hasSoleTraderInfo,
  hasApplicant,
  (hasSoleTrader, hasApplicant) => hasSoleTrader || hasApplicant,
);

export const hasSubmittedStakeholderBlockFca = hasSubmittedStakeholderBlockSfsa;
export const hasSubmittedStakeholderBlockAu = hasSubmittedStakeholderBlockSfsa;

export const hasSubmittedStakeholderBlock = country =>
  createSelector(
    merchantRegion,
    hasSubmittedStakeholderBlockAu,
    hasSubmittedStakeholderBlockFca,
    hasSubmittedStakeholderBlockSfsa,
    hasSubmittedStakeholderBlockUs,
    hasSoleProprietorshipInfo,
    (
      region,
      hasSubmittedStakeholderBlockAu,
      hasSubmittedStakeholderBlockFca,
      hasSubmittedStakeholderBlockSfsa,
      hasSubmittedStakeholderBlockUs,
      hasSoleProprietorshipInfo,
    ) => {
      if (region === 'eu') {
        if (country === 'GB') {
          return hasSubmittedStakeholderBlockFca;
        }
        if (country === 'US') {
          return hasSubmittedStakeholderBlockSfsa || hasSoleProprietorshipInfo;
        }
        return hasSubmittedStakeholderBlockSfsa;
      }
      if (region === 'us') {
        return hasSubmittedStakeholderBlockUs;
      }
      if (region === 'ap') {
        return hasSubmittedStakeholderBlockAu;
      }
    },
  );

export const canSubmitApplication = country =>
  createSelector(
    merchantRegion,
    hasBankAccounts,
    hasDocuments,
    hasOrganizationEntityTypeInfo,
    hasRegistrationInformation,
    hasSelectedProduct,
    hasStoreDetails,
    hasSubmittedStakeholderBlock(country),
    hasVatInformation,
    isManagedKyc,
    isNativeKyc,
    isUnmanagedKyc,
    hasMandatoryDocuments,
    getForcedRegistrationIdCountries,
    getForcedVatIdCountries,
    getEnrichedVatCountries,
    (
      region,
      hasBankAccounts,
      hasDocuments,
      hasOrganizationEntityTypeInfo,
      hasRegistrationInformation,
      hasSelectedProduct,
      hasStoreDetails,
      hasSubmittedStakeholderBlock,
      hasVatInformation,
      isManagedKyc,
      isNativeKyc,
      isUnmanagedKyc,
      hasMandatoryDocuments,
      forcedRegistrationIdCountries,
      forcedVatIdCountries,
      enrichesVatCountries,
    ) => {
      let canSubmit;

      if (isUnmanagedKyc) {
        canSubmit =
          hasSelectedProduct && hasStoreDetails && hasSubmittedStakeholderBlock && hasBankAccounts;
      } else if (isNativeKyc) {
        canSubmit = hasStoreDetails && hasSubmittedStakeholderBlock && hasBankAccounts;
      } else if (isManagedKyc) {
        canSubmit =
          region === 'us' ? hasStoreDetails : hasStoreDetails && hasSubmittedStakeholderBlock;
      }

      if (forcedRegistrationIdCountries.includes(country)) {
        canSubmit = canSubmit && hasRegistrationInformation;
      }
      if (forcedVatIdCountries.includes(country)) {
        canSubmit = canSubmit && (hasVatInformation || enrichesVatCountries.includes(country));
      }

      if (region === 'us') {
        // For countries that are not the US (cross border) we don't need to collect
        // organization entity type.
        return country === 'US' ? canSubmit && hasOrganizationEntityTypeInfo : canSubmit;
      }

      if (hasMandatoryDocuments) {
        canSubmit = canSubmit && hasDocuments;
      }

      return canSubmit;
    },
  );

export const getPathFromEntityType = createSelector(
  getBusinessDetails,
  ({ entityType, registered }) => {
    const PATHS = {
      APPLICATION_CONFIRMATION: 'application-confirmation',
      ENTITY_TYPE_SELECTION: 'entity-type-selection',
    };
    if (!entityType && !registered) return PATHS.ENTITY_TYPE_SELECTION;
    return PATHS.APPLICATION_CONFIRMATION;
  },
);

export const isMerchantTransactionsEnabled = createSelector(
  getMerchantStatus,
  status => status === ONBOARDING_STATUSES_V2.TRANSACTIONS_ENABLED,
);

export const isMerchantTransactionsReviewAutomated = createSelector(
  getMerchantStatus,
  status => status === ONBOARDING_STATUSES_V2.TRANSACTIONS_REVIEW_AUTOMATED,
);

export const isMerchantTransactionsReviewManual = createSelector(
  getMerchantStatus,
  status => status === ONBOARDING_STATUSES_V2.TRANSACTIONS_REVIEW_MANUAL,
);

export const isMerchantTransactionsReviewAutomatedOrManual = createSelector(
  getMerchantStatus,
  status =>
    status === ONBOARDING_STATUSES_V2.TRANSACTIONS_REVIEW_AUTOMATED ||
    status === ONBOARDING_STATUSES_V2.TRANSACTIONS_REVIEW_MANUAL,
);

export const isApplicationSubmitted = createSelector(
  getMerchantStatus,
  status =>
    ONBOARDING_STATUS_VALUES_V2[status] >=
    ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_REVIEW_AUTOMATED,
);

export const isNotPayoutsEnabled = createSelector(
  getMerchantStatus,
  status =>
    ONBOARDING_STATUS_VALUES_V2[status] <= ONBOARDING_STATUS_VALUES_V2.PAYOUT_DETAILS_STARTED,
);

export const ifIsRegisteredShouldHaveCompanyDetails = createSelector(
  isMerchantRegistered,
  hasCompanyDetails,
  (registered, hasCompanyDetails) => (registered ? hasCompanyDetails : true),
);

export const getAfterFlowStepForMerchantApplication = createSelector(
  isWidget,
  isUnmanagedKyc,
  (isWidget, isUnmanagedKyc) => getAfterFlowStep({ isUnmanagedKyc, isWidget }),
);

export const isSoleTraderOrSoleProprietorship = createSelector(
  isOrganizationEntityTypeSoleProprietorship,
  isEntityTypeSoleTrader,
  (isSoleProprietorship, isSoleTrader) => isSoleTrader || isSoleProprietorship,
);
